import React from 'react';
import AdminPage from 'lib/admin-kit/components/AdminPage';
import AdminTable from 'lib/admin-kit/components/AdminTable';
import PeriodSelector from './PeriodSelector';
import { formatCost, formatCount } from './format';

const INDEX_ENDPOINT = '/internal_admin/admin_kit/ai_usage_logs';

const featureColumns = [
  {
    name: 'feature',
    title: 'Feature',
    DataCell: ({ rowData }) => rowData.feature ?? '—',
  },
  {
    name: 'subtype',
    title: 'Subtype',
    DataCell: ({ rowData }) => rowData.subtype ?? '—',
  },
  {
    name: 'count',
    title: 'Calls',
    DataCell: ({ rowData }) => formatCount(rowData.count),
  },
  {
    name: 'input_tokens',
    title: 'Input Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.input_tokens),
  },
  {
    name: 'output_tokens',
    title: 'Output Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.output_tokens),
  },
  {
    name: 'cost_usd',
    title: 'Cost',
    DataCell: ({ rowData }) => formatCost(rowData.cost_usd),
  },
];

// Rows come back flat from AiUsageLogs::UserAggregate (one per
// dealer/feature/subtype combo) — group them here into one section per
// location, ordered by that location's total cost, so the user's activity
// reads as "here's everywhere they used it, and what they did there".
const groupByDealer = (rows) => {
  const byDealer = new Map();

  rows.forEach((row) => {
    const key = row.dealer_id ?? '';
    if (!byDealer.has(key)) {
      byDealer.set(key, {
        dealerId: row.dealer_id,
        dealerName: row.dealer_name,
        rows: [],
        totals: { count: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0 },
      });
    }
    const group = byDealer.get(key);
    group.rows.push(row);
    group.totals.count += row.count;
    group.totals.input_tokens += row.input_tokens;
    group.totals.output_tokens += row.output_tokens;
    group.totals.cost_usd += row.cost_usd;
  });

  return Array.from(byDealer.values()).sort(
    (a, b) => b.totals.cost_usd - a.totals.cost_usd,
  );
};

const UserShow = ({
  userId,
  userEmail,
  rows = [],
  totals = {},
  period = 'day',
}) => {
  const groups = groupByDealer(rows);

  return (
    <AdminPage
      title={userEmail ?? userId}
      breadcrumbs={[
        { text: 'AI Usage', href: INDEX_ENDPOINT },
        { text: userEmail ?? userId },
      ]}
    >
      <PeriodSelector
        period={period}
        basePath={`${INDEX_ENDPOINT}/users/${userId}`}
      />

      <div
        style={{
          fontSize: 13,
          color: '#555',
          padding: '4px 0',
          marginBottom: 16,
        }}
      >
        Total: {formatCount(totals.count)} calls,{' '}
        {formatCount(totals.input_tokens)} input tokens,{' '}
        {formatCount(totals.output_tokens)} output tokens,{' '}
        {formatCost(totals.cost_usd)}
      </div>

      {groups.length === 0 && (
        <div>No AI usage recorded for this user in this period.</div>
      )}

      {groups.map((group) => (
        <div key={group.dealerId ?? 'unknown'} style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 4,
            }}
          >
            <strong>
              Location:{' '}
              {group.dealerId ? (
                <a
                  href={`${INDEX_ENDPOINT}/${group.dealerId}`}
                  style={{ textDecoration: 'underline' }}
                >
                  {group.dealerName ?? group.dealerId}
                </a>
              ) : (
                group.dealerName ?? '—'
              )}
            </strong>
            <span style={{ fontSize: 13, color: '#555' }}>
              Total: {formatCount(group.totals.count)} calls,{' '}
              {formatCost(group.totals.cost_usd)}
            </span>
          </div>
          <AdminTable
            columns={featureColumns}
            data={group.rows}
            getRowKey={(r) => `${r.dealer_id}-${r.feature}-${r.subtype}`}
          />
        </div>
      ))}
    </AdminPage>
  );
};

export default UserShow;
